import { appConfig } from '../config';
import { messageService } from '../message';
import { translations } from '../i18n';
import * as Logger from '@navigator/frontend-logger';
import { Connectivity } from '../../third-party/service/navigator/navigator-services'

export const TIMEOUT_RETRY = 10 * 1000

export class KeepAliveService {
  public logoutUrl: string;
  private url: string;
  private timeout: number;
  private intervalHandle: any;
  private redirectUrl: string;
  private onSuccess: (data: Response) => void = null;
  private onFail: (error: any) => void = null;
  private retry = false;

  constructor() {
    this.logoutUrl = this.getLogoutUrl();
    this.redirectUrl = this.getRedirectUrl();
    /* istanbul ignore next else */
    if (appConfig.core) {
      // this will be called faster, so we can test things out
      this.timeout = appConfig.core.keepAliveInterval;
      this.url = appConfig.core.keepAliveUrl;
    }
  }

  private getLogoutUrl(): string | undefined {
    const thirdPartyConfig = appConfig.thirdParty;
    return thirdPartyConfig?.navigator?.session_expired_url;
  }

  private getRedirectUrl(): string | undefined {
    const coreConfig = appConfig.core;
    return coreConfig?.keepAliveFailedRedirectTargetUrl;
  }

  /*
   * @type method
   * @name @poseidon/pos-keep-alive/PosKeepAliveService#start
   *
   * @description
   * Initializes the keep-alive request interval
   *
   * @param {Function} [success=null] Callback to execute on successful keepAlive request
   * @param {Function} [fail=null] Callback to execute on failed keepAlive request
   */
  public start(success: any = null, fail: any = null): void {
    this.onSuccess = success;
    this.onFail = fail;
    if (this.url && this.timeout) {
      this.intervalHandle = window.setInterval(() => {
          this.doRequest();
      }, this.timeout);
    }

    if (this.url) {
      document.addEventListener('visibilitychange', () => {
        /* istanbul ignore next else */
        if (document.visibilityState === 'visible') {
          this.doRequest()
        }
      });
    }
  }

  /*
   * @type method
   * @name @poseidon/pos-keep-alive/PosKeepAliveService#stop
   *
   * @description
   * Stops keep-alive request calls
   */
  public stop(): void {
    if (this.intervalHandle) {
      window.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }
  }

  /*
   * @type method
   * @name @poseidon/pos-keep-alive/PosKeepAliveService#doRequest
   *
   * @description
   * Do the keep-alive request.
   *
   * @return {Promise} Promise of a request call
   */
  public async doRequest(): Promise<any> {
    if (Connectivity.offline() === true) {
      return
    }

    const onSuccess = (data: Response) => {
      this.retry = false
      if (this.onSuccess) {
        this.onSuccess(data);
      }
    };

    const onFail = (error: any) => {
      if (this.onFail) {
        this.onFail(error);
      }

      // only display message if user was logged out [SWAT-5867]
      if (error?.status === 403) {
        this.stop();
        this.showWarningOrRedirect()
      } else {
        if (!this.retry) {
          // retry once soon after failure [SWAT-5867]
          this.retry = true
          setTimeout(() => {
            this.doRequest()
          }, TIMEOUT_RETRY)
        }
      }
    };

    let res: Response
    try {
      res = await fetch(this.url, {
        method: 'POST',
        credentials: 'same-origin'
      })

      if (res.status >= 400) {
        onFail(res);
      } else {
        onSuccess(res);
      }
    } catch (reason) {
      onFail(reason);
    }

  }

  public showWarningOrRedirect() {
    if (this.redirectUrl) {
      window.location.href = this.redirectUrl
    } else {
      this.showLoggedOutMessage()
    }
  }

  public showLoggedOutMessage() {
    let config = {}
    /* istanbul ignore next else */
    if (translations.LOGGED_OUT_AUTOMATICALLY_TITLE || translations.LOGGED_OUT_AUTOMATICALLY_TEXT) {
      config = {
        heading: translations.LOGGED_OUT_AUTOMATICALLY_TITLE,
        detailsHtml: translations.LOGGED_OUT_AUTOMATICALLY_TEXT,
      }

      if (translations.GO_TO_LOGIN_TEXT && this.logoutUrl) {
        Object.assign(config, {
          primaryText: translations.GO_TO_LOGIN_TEXT,
          primaryCallback: () => {
            /* istanbul ignore next */
            if (this.logoutUrl) {
              window.location.href = this.logoutUrl;
            }
          },
        })
      }

      if (translations.MORE_INFORMATION_TEXT && translations.MORE_INFORMATION_LINK) {
        Object.assign(config, {
          secondaryText: translations.MORE_INFORMATION_TEXT,
          secondaryCallback: () => {
            window.open(translations.MORE_INFORMATION_LINK, '_blank').focus()
          },
        })
      }

      messageService.showWarning(config);
    } else {
      Logger.error('KeepAliveService: Missing translations for automatic logout message!');
    }
  }
}

export const keepAliveService = new KeepAliveService();
